import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ExternalLink, CheckCircle2, Globe, Mail, Phone, Inbox } from 'lucide-react';
import { STATUS_STYLES } from './constants';

export default function ExternalApplicantsTab({ apps, postings, api, refresh }) {
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [postingFilter, setPostingFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/staffing/applications/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      refresh();
    } catch { toast.error('Failed to update'); }
  };

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return apps.filter(a => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (postingFilter !== 'all' && a.posting_id !== postingFilter) return false;
      if (q && !(a.user_name||'').toLowerCase().includes(q) && !(a.user_email||'').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [apps, statusFilter, postingFilter, search]);

  const counts = React.useMemo(() => apps.reduce((acc, a) => {
    acc.total++; acc[a.status] = (acc[a.status]||0)+1; return acc;
  }, { total: 0 }), [apps]);

  return (
    <div className="mt-5 space-y-4" data-testid="external-applicants-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { key:'total', label:'Total', cls:'bg-[#EDE7F6] text-[#6B3FA0]' },
          { key:'pending', label:'Pending', cls:'bg-[#FFF3E0] text-[#E65100]' },
          { key:'reviewed', label:'Reviewed', cls:'bg-[#E3F2FD] text-[#1565C0]' },
          { key:'accepted', label:'Accepted', cls:'bg-[#E8F5E9] text-[#1B5E20]' },
        ].map(c => (
          <div key={c.key} className={`rounded-lg p-3 ${c.cls}`} data-testid={`ext-stat-${c.key}`}>
            <div className="text-2xl font-extrabold">{counts[c.key]||0}</div>
            <div className="text-[11px] uppercase tracking-wide font-semibold opacity-80">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <Input placeholder="Search name or email..." value={search} onChange={e=>setSearch(e.target.value)} className="border-[#DED8EB] sm:max-w-xs" data-testid="ext-search"/>
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} className="border border-[#DED8EB] rounded-md px-3 py-2 text-sm bg-white" data-testid="ext-status-filter">
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={postingFilter} onChange={e=>setPostingFilter(e.target.value)} className="border border-[#DED8EB] rounded-md px-3 py-2 text-sm bg-white sm:max-w-xs" data-testid="ext-posting-filter">
          <option value="all">All postings</option>
          {postings.filter(p=>p.is_public).map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <Card className="border-dashed border-[#DED8EB]"><CardContent className="p-10 text-center">
          <Inbox className="h-10 w-10 mx-auto text-[#9B7ED8] opacity-60 mb-2"/>
          <p className="text-sm font-semibold text-[#2D1B4E]">No external applicants yet</p>
          <p className="text-xs text-[#8E85A0] mt-1">Mark a posting as <strong>Public</strong> and share <span className="font-mono text-[#6B3FA0]">/careers</span> to start receiving applications.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(a => (
            <Card key={a.id} className="border-[#DED8EB] shadow-sm" data-testid={`ext-app-card-${a.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-[#2D1B4E]">{a.user_name}</h3>
                      <Badge className={`text-[9px] ${STATUS_STYLES[a.status]||STATUS_STYLES.draft}`}>{a.status}</Badge>
                      <Badge className="text-[9px] bg-[#E0F2F1] text-[#00695C] gap-1"><Globe className="h-2.5 w-2.5"/>External</Badge>
                    </div>
                    {a.posting_title && <p className="text-xs text-[#6B3FA0] font-semibold mt-0.5">{a.posting_title}</p>}
                    <div className="flex items-center gap-3 mt-1 flex-wrap text-[11px] text-[#5A4D6E]">
                      <a href={`mailto:${a.user_email}`} className="flex items-center gap-1 hover:text-[#6B3FA0]" data-testid={`ext-email-${a.id}`}><Mail className="h-3 w-3"/>{a.user_email}</a>
                      {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-1 hover:text-[#6B3FA0]"><Phone className="h-3 w-3"/>{a.phone}</a>}
                      {a.resume_url && <a href={a.resume_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#6B3FA0] hover:underline font-semibold" data-testid={`ext-resume-${a.id}`}><ExternalLink className="h-3 w-3"/>Resume / Profile</a>}
                      <span className="text-[#8E85A0]">{(a.created_at||'').slice(0,10)}</span>
                    </div>
                  </div>
                </div>
                {a.message && <div className="bg-[#F7F4EF] border border-[#DED8EB] rounded-md p-2"><p className="text-[11px] text-[#8E85A0] uppercase font-semibold mb-1">Cover letter</p><p className="text-xs text-[#2D1B4E] whitespace-pre-wrap">{a.message}</p></div>}
                <div className="flex flex-wrap gap-1 pt-1">
                  <Button size="sm" variant="outline" onClick={()=>updateStatus(a.id,'reviewed')} className="h-7 text-[10px]" data-testid={`ext-review-${a.id}`}>Mark reviewed</Button>
                  <Button size="sm" variant="outline" onClick={()=>updateStatus(a.id,'accepted')} className="h-7 text-[10px] text-[#1B5E20] border-[#81C784]" data-testid={`ext-accept-${a.id}`}><CheckCircle2 className="h-3 w-3 mr-0.5"/>Accept</Button>
                  <Button size="sm" variant="outline" onClick={()=>updateStatus(a.id,'rejected')} className="h-7 text-[10px] text-[#D9534F] border-red-300" data-testid={`ext-reject-${a.id}`}>Reject</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
